/**
 * AutoCount - Order Detail Page
 */

import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import { Link, useParams } from "react-router-dom";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

interface OrderItem {
  partNumber: string;
  name: string;
  qty: number;
  unitPrice: string;
  total: string;
}

interface OrderRecord {
  orderNumber: string;
  status: string;
  customer: string;
  email: string;
  phone: string;
  address1: string;
  address2: string;
  items: OrderItem[];
  subtotal: string;
  tax: string;
  shipping: string;
  total: string;
  paymentStatus: string;
}

const ORDERS: Record<string, OrderRecord> = {
  "1": {
    orderNumber: "ORD-2026-001",
    status: "Processing",
    customer: "Joe's Auto Repair",
    email: "joe@joesautorepair.com",
    phone: "(555) 123-4567",
    address1: "123 Mechanic Street",
    address2: "Detroit, MI 48201",
    items: [
      { partNumber: "BP-TC-2024-FR", name: "Front Brake Pads - Toyota Camry", qty: 2, unitPrice: "$69.99", total: "$139.98" },
      { partNumber: "OF-TY-2024", name: "Oil Filter - Toyota Universal", qty: 2, unitPrice: "$12.99", total: "$25.98" },
      { partNumber: "SP-NGK-IRIDIUM-4", name: "Iridium Spark Plugs - 4 Pack", qty: 1, unitPrice: "$49.99", total: "$49.99" },
    ],
    subtotal: "$215.95",
    tax: "$12.96",
    shipping: "$12.99",
    total: "$241.90",
    paymentStatus: "Paid",
  },
  "2": {
    orderNumber: "ORD-2026-002",
    status: "Shipped",
    customer: "Quick Lube Plus",
    email: "service@quicklubeplus.com",
    phone: "(555) 234-5678",
    address1: "88 Express Lane",
    address2: "Warren, MI 48088",
    items: [
      { partNumber: "OF-TY-2024", name: "Oil Filter - Toyota Universal", qty: 8, unitPrice: "$12.99", total: "$103.92" },
      { partNumber: "AF-FD-F150-21", name: "Engine Air Filter - Ford F-150", qty: 2, unitPrice: "$19.99", total: "$39.98" },
      { partNumber: "CF-HN-CRV-22", name: "Cabin Air Filter - Honda CR-V", qty: 2, unitPrice: "$24.99", total: "$49.98" },
    ],
    subtotal: "$193.88",
    tax: "$11.63",
    shipping: "$19.99",
    total: "$225.50",
    paymentStatus: "Paid",
  },
  "3": {
    orderNumber: "ORD-2026-003",
    status: "Pending",
    customer: "Metro Auto Service",
    email: "parts@metroautoservice.com",
    phone: "(555) 345-6789",
    address1: "450 Gratiot Avenue",
    address2: "Detroit, MI 48226",
    items: [
      { partNumber: "BP-HC-2024-FR", name: "Front Brake Pads - Honda Civic", qty: 2, unitPrice: "$64.99", total: "$129.98" },
      { partNumber: "BR-TC-2024-FR", name: "Front Brake Rotor - Toyota Camry", qty: 2, unitPrice: "$89.99", total: "$179.98" },
      { partNumber: "BAT-H6-AGM", name: "AGM Battery Group H6 (48)", qty: 1, unitPrice: "$199.99", total: "$199.99" },
      { partNumber: "OF-TY-2024", name: "Oil Filter - Toyota Universal", qty: 3, unitPrice: "$12.99", total: "$38.97" },
    ],
    subtotal: "$548.92",
    tax: "$32.94",
    shipping: "$24.99",
    total: "$606.85",
    paymentStatus: "Pending",
  },
  "4": {
    orderNumber: "ORD-2026-004",
    status: "Delivered",
    customer: "Highway Motors",
    email: "office@highwaymotors.com",
    phone: "(555) 456-7890",
    address1: "7200 Telegraph Road",
    address2: "Taylor, MI 48180",
    items: [
      { partNumber: "ALT-GM-160A", name: "Alternator 160A - GM Trucks", qty: 1, unitPrice: "$219.99", total: "$219.99" },
      { partNumber: "BAT-H6-AGM", name: "AGM Battery Group H6 (48)", qty: 1, unitPrice: "$199.99", total: "$199.99" },
      { partNumber: "SP-NGK-IRIDIUM-4", name: "Iridium Spark Plugs - 4 Pack", qty: 1, unitPrice: "$49.99", total: "$49.99" },
    ],
    subtotal: "$469.97",
    tax: "$28.20",
    shipping: "$14.99",
    total: "$513.16",
    paymentStatus: "Paid",
  },
};

function OrdersDetail(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const order = ORDERS[id ?? "1"] ?? ORDERS["1"];

  const columns = [
    { Header: "Part Number", accessor: "partNumber", width: "20%" },
    { Header: "Name", accessor: "name", width: "35%" },
    { Header: "Qty", accessor: "qty", width: "10%" },
    { Header: "Unit Price", accessor: "unitPrice", width: "15%" },
    { Header: "Total", accessor: "total", width: "15%" },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12} lg={8}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  Order #{order.orderNumber}
                </MDTypography>
                <MDTypography variant="caption" color="white">
                  {order.status}
                </MDTypography>
              </MDBox>
              <MDBox p={3}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <MDTypography variant="h6" mb={1}>Customer</MDTypography>
                    <MDTypography variant="body2">{order.customer}</MDTypography>
                    <MDTypography variant="body2" color="text">{order.email}</MDTypography>
                    <MDTypography variant="body2" color="text">{order.phone}</MDTypography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <MDTypography variant="h6" mb={1}>Shipping Address</MDTypography>
                    <MDTypography variant="body2">{order.address1}</MDTypography>
                    <MDTypography variant="body2" color="text">{order.address2}</MDTypography>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                <MDTypography variant="h6" mb={2}>Order Items</MDTypography>
                <DataTable
                  table={{ columns, rows: order.items }}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={false}
                />
              </MDBox>
            </Card>
          </Grid>

          <Grid item xs={12} lg={4}>
            <Card>
              <MDBox p={3}>
                <MDTypography variant="h6" mb={2}>Order Summary</MDTypography>
                <MDBox mb={1} display="flex" justifyContent="space-between">
                  <MDTypography variant="body2">Subtotal:</MDTypography>
                  <MDTypography variant="body2">{order.subtotal}</MDTypography>
                </MDBox>
                <MDBox mb={1} display="flex" justifyContent="space-between">
                  <MDTypography variant="body2">Tax (6%):</MDTypography>
                  <MDTypography variant="body2">{order.tax}</MDTypography>
                </MDBox>
                <MDBox mb={1} display="flex" justifyContent="space-between">
                  <MDTypography variant="body2">Shipping:</MDTypography>
                  <MDTypography variant="body2">{order.shipping}</MDTypography>
                </MDBox>
                <Divider sx={{ my: 2 }} />
                <MDBox display="flex" justifyContent="space-between">
                  <MDTypography variant="h6">Total:</MDTypography>
                  <MDTypography variant="h6" color="info">{order.total}</MDTypography>
                </MDBox>

                <MDBox mt={3}>
                  <MDTypography variant="caption" color="text">Payment Status</MDTypography>
                  <MDTypography
                    variant="body2"
                    color={order.paymentStatus === "Paid" ? "success" : "warning"}
                    fontWeight="medium"
                  >
                    {order.paymentStatus}
                  </MDTypography>
                </MDBox>

                <MDBox mt={3} display="flex" flexDirection="column" gap={1}>
                  <MDButton variant="gradient" color="info" fullWidth onClick={() => window.print()}>
                    Print Invoice
                  </MDButton>
                  <MDButton component={Link} to="/orders" variant="outlined" color="secondary" fullWidth>
                    Back to Orders
                  </MDButton>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default OrdersDetail;
